import { useTranslation } from 'react-i18next';
import GridContainer from '@/components/Grid/GridContainer';
import GridItem from '@/components/Grid/GridItem';
import Card from '@/components/Card/Card';
import CardHeader from '@/components/Card/CardHeader';
import CardBody from '@/components/Card/CardBody';
import { useEffect, useState } from 'react';
import loader from '@/assets/img/loader.svg';
import { Link } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';
import { API_BASE_URL } from '@/config';
import moment from 'moment';
import { REFUND_TYPES } from '@/utility';
import TablePaginationActions from '@/views/Managefile/TablePaginationActions/TablePaginationActions';
import { checkPermission } from '@/views/Facilities/CheckPermissions';

export default function DataMappings() {
  const { t } = useTranslation();
  const [loaded] = useState(false);
  const [dataMappings, setDataMappings] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);

  const getDataMappings = async () => {
    try {
      const response = await axios.get(API_BASE_URL + '/data-mappings');
      setDataMappings(response.data);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      await getDataMappings();
    };
    fetchData();
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const deleteDataMapping = async (id) => {
    try {
      const isConfirmed = window.confirm(t('dataMappings.confirmDelete'));
      if (!isConfirmed) {
        return;
      }
      await axios.delete(API_BASE_URL + '/data-mappings/' + id);
      await getDataMappings();
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <GridContainer>
      <GridItem xs={12} sm={12} md={12}>
        <Card>
          <CardHeader color='primary'>
            <h4>{t('dataMappings.title')}</h4>
          </CardHeader>

          <CardBody>
            <div>
              {loaded && (
                <div className='loaders'>
                  <img src={loader} alt={t('common.loading')} />
                </div>
              )}
              <div className='t-header'>
                <div className='t-header-right'>
                  <Link to={`/facility/create-data-mapping`} className='link'>
                    <span className='add-icon'>
                      <AddIcon color='primary' />
                    </span>
                    {t('dataMappings.createButton')}
                  </Link>
                </div>
              </div>

              <Table width='100%' id='template'>
                <TableHead>
                  <TableRow>
                    <TableCell align='left'>
                      {t('dataMappings.table.columns.name')}
                    </TableCell>
                    <TableCell align='left'>
                      {t('dataMappings.table.columns.lastUpdatedBy')}
                    </TableCell>
                    <TableCell align='left'>
                      {t('dataMappings.table.columns.lastUpdatedAt')}
                    </TableCell>
                    <TableCell align='left'>
                      {t('dataMappings.table.columns.facilityName')}
                    </TableCell>
                    <TableCell align='left'>
                      {t('dataMappings.table.columns.fileDelimiter')}
                    </TableCell>
                    <TableCell align='left'>
                      {t('dataMappings.table.columns.paymentTypes')}
                    </TableCell>
                    <TableCell align='left'>
                      {t('dataMappings.table.columns.sftpEnabled')}
                    </TableCell>
                    {checkPermission({
                      parentKey: 'client_bank_accounts',
                      childKey: 'view_bank_accounts',
                      permission: 'read',
                    }) && (
                      <TableCell align='left'>
                        {t('dataMappings.table.columns.bankAccount')}
                      </TableCell>
                    )}
                    <TableCell align='left'>
                      {t('dataMappings.table.columns.actions')}
                    </TableCell>
                  </TableRow>
                </TableHead>

                {dataMappings.length ? (
                  <TableBody>
                    {(rowsPerPage > 0
                      ? dataMappings.slice(
                          page * rowsPerPage,
                          page * rowsPerPage + rowsPerPage,
                        )
                      : dataMappings
                    ).map((dataMapping, index) => (
                      <TableRow className='txtalign' key={index}>
                        <TableCell>{dataMapping.name}</TableCell>
                        <TableCell>{dataMapping.updated_by}</TableCell>
                        <TableCell>
                          {moment(dataMapping.updated_at).format('MM/DD/YYYY')}
                        </TableCell>
                        <TableCell>
                          {dataMapping.facility_name || '-'}
                        </TableCell>
                        <TableCell>{dataMapping.delimiter}</TableCell>
                        <TableCell>
                          {dataMapping.refund_types
                            .map((refundType) =>
                              refundType === REFUND_TYPES.REGULAR
                                ? t('dataMappings.paymentTypes.consumer')
                                : refundType === REFUND_TYPES.INSURANCE
                                ? t('dataMappings.paymentTypes.insurance')
                                : refundType === REFUND_TYPES.BUSINESS
                                ? t('dataMappings.paymentTypes.business')
                                : refundType,
                            )
                            .join(', ')}
                        </TableCell>

                        <TableCell>
                          {dataMapping.is_sftp_enabled
                            ? t('common.yes')
                            : t('common.no')}
                        </TableCell>

                        {checkPermission({
                          parentKey: 'client_bank_accounts',
                          childKey: 'view_bank_accounts',
                          permission: 'read',
                        }) && (
                          <TableCell>
                            {dataMapping.bank_account_display_name ? (
                              <>
                                {dataMapping.bank_account_display_name}
                                {dataMapping.bank_account_last_four &&
                                  ` (****${dataMapping.bank_account_last_four})`}
                              </>
                            ) : (
                              '-'
                            )}
                          </TableCell>
                        )}

                        <TableCell>
                          <Link
                            to={`/facility/edit-data-mapping/${dataMapping.id}`}
                            title={t('dataMappings.actions.update')}>
                            <EditIcon color='primary' />
                          </Link>

                          <a
                            style={{ cursor: 'pointer', marginLeft: 8 }}
                            onClick={() => deleteDataMapping(dataMapping.id)}
                            title={t('dataMappings.actions.delete')}>
                            <DeleteIcon color='error' />
                          </a>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                ) : (
                  <TableBody>
                    <TableRow>
                      <TableCell>{t('dataMappings.emptyState')}</TableCell>
                    </TableRow>
                  </TableBody>
                )}
                <TableFooter>
                  <TableRow align='center'>
                    <TablePagination
                      rowsPerPageOptions={[
                        5,
                        10,
                        25,
                        { label: t('common.all'), value: -1 },
                      ]}
                      colSpan={3}
                      count={dataMappings.length}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      labelRowsPerPage={t('dataMappings.table.rowsPerPage')}
                      SelectProps={{
                        inputProps: { roboto: 'rows per page' },
                        native: true,
                      }}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleRowsPerPage}
                      ActionsComponent={TablePaginationActions}
                    />
                  </TableRow>
                </TableFooter>
              </Table>
            </div>
          </CardBody>
        </Card>
      </GridItem>
    </GridContainer>
  );
}
